/* global google, gapi */
import { getKey } from '../keys.js'

const CLIENT_ID = getKey("google", "client_id")

const SCOPES = [
  "email",
  "https://www.googleapis.com/auth/drive.file",
].join(" ")

const DISCOVERY_DOCS = [
  "https://www.googleapis.com/discovery/v1/apis/drive/v3/rest",
  "https://sheets.googleapis.com/$discovery/rest?version=v4"
]

let tokenClient = null
let accessToken = null
let currentUserProfile = null
let signInListeners = []

const waitForGoogleIdentity = function() {
  return new Promise((resolve) => {
    const check = () => {
      if (typeof google !== 'undefined' && google.accounts && google.accounts.oauth2) {
        resolve()
      } else {
        setTimeout(check, 100)
      }
    }
    check()
  })
}

const loadGapiClient = function() {
  return new Promise((resolve, reject) => {
    gapi.load('client:picker', {
      callback: resolve,
      onerror: reject,
      timeout: 5000,
      ontimeout: () => reject(new Error('gapi.client timed out'))
    })
  })
}

const initGapiClient = function() {
  return gapi.client.init({}).then(() => {
    return gapi.client.load(DISCOVERY_DOCS[0]).then(() => {
      return gapi.client.load(DISCOVERY_DOCS[1])
    })
  })
}

const ensureGapi = function() {
  return loadGapiClient().then(() => initGapiClient())
}

const initTokenClient = function() {
  return waitForGoogleIdentity().then(() => {
    return new Promise((resolve) => {
      tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: SCOPES,
        callback: () => {}
      })
      resolve()
    })
  })
}

const fetchUserProfile = function() {
  return fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
    headers: { Authorization: `Bearer ${accessToken}` }
  }).then(res => res.json()).then(data => {
    currentUserProfile = {
      name: data.name || data.email,
      email: data.email,
      imageUrl: data.picture
    }
  })
}

const getClient = function(callback) {
  return ensureGapi().then(() => callback(gapi.client))
}

const registerSignInListener = function(listener) {
  signInListeners.push(listener)
  return Promise.resolve()
}

const isSignedIn = function() {
  return Promise.resolve(!!accessToken)
}

const getAccessToken = function() {
  return Promise.resolve(accessToken)
}

const getCurrentUser = function() {
  return Promise.resolve(currentUserProfile)
}

const getCurrentUserProfile = function() {
  return Promise.resolve(currentUserProfile)
}

const restoreToken = function(token) {
  return ensureGapi().then(() => {
    accessToken = token
    gapi.client.setToken({ access_token: token })
    return fetchUserProfile()
  })
}

const signIn = function() {
  return Promise.all([ensureGapi(), initTokenClient()]).then(() => {
    return new Promise((resolve, reject) => {
      tokenClient.callback = (tokenResponse) => {
        if (tokenResponse && tokenResponse.access_token) {
          accessToken = tokenResponse.access_token
          gapi.client.setToken({ access_token: accessToken })
          const expiry = Date.now() + (tokenResponse.expires_in * 1000)
          localStorage.setItem('papartisan_token', accessToken)
          localStorage.setItem('papartisan_token_expiry', expiry)
          fetchUserProfile().then(() => {
            signInListeners.forEach(listener => listener(true))
            resolve(currentUserProfile)
          })
        } else {
          reject(new Error('Sign in failed'))
        }
      }
      tokenClient.requestAccessToken({ prompt: '' })
    })
  })
}

const refreshToken = function() {
  return Promise.all([ensureGapi(), initTokenClient()]).then(() => {
    return new Promise((resolve, reject) => {
      tokenClient.callback = (tokenResponse) => {
        if (tokenResponse.error) {
          reject(new Error(tokenResponse.error))
          return
        }
        if (tokenResponse && tokenResponse.access_token) {
          accessToken = tokenResponse.access_token
          gapi.client.setToken({ access_token: accessToken })
          const expiry = Date.now() + (tokenResponse.expires_in * 1000)
          localStorage.setItem('papartisan_token', accessToken)
          localStorage.setItem('papartisan_token_expiry', expiry)
          resolve()
        } else {
          reject(new Error('No access token in response'))
        }
      }
      tokenClient.requestAccessToken({ prompt: 'none' })
    })
  })
}

const signOut = function() {
  accessToken = null
  currentUserProfile = null
  gapi.client.setToken(null)
  signInListeners.forEach(listener => listener(false))
  return Promise.resolve()
}

const revokeAccess = function() {
  return waitForGoogleIdentity().then(() => {
    return new Promise((resolve) => {
      const tokenToRevoke = accessToken
      signOut()
      localStorage.removeItem('papartisan_token')
      localStorage.removeItem('papartisan_token_expiry')
      if (tokenToRevoke) {
        google.accounts.oauth2.revoke(tokenToRevoke, resolve)
      } else {
        resolve()
      }
    })
  })
}

export default { getClient, registerSignInListener, isSignedIn, getCurrentUser, getCurrentUserProfile, getAccessToken, signIn, refreshToken, restoreToken, signOut, revokeAccess }
export { getClient, registerSignInListener, isSignedIn, getCurrentUser, getCurrentUserProfile, getAccessToken, signIn, refreshToken, restoreToken, signOut, revokeAccess }